<template>
  <div class="modal-mask" @click.self="onCancel" v-if="show">
    <div class="modal-container">
      <div class="modal-title">{{ modalTitle }}</div>
      <div class="modal-content">
        <div class="row">
          <label class="label">{{ t("bindRole.server") }}</label>
          <div class="select-container">
            <select
              v-model="selectedServerId"
              class="select-input"
              @change="onServerChange"
            >
              <option value="">{{ t("bindRole.serverPlaceholder") }}</option>
              <optgroup :label="t('bindRole.recentServers')">
                <option
                  v-for="server in bindStore.recentServerList"
                  :key="server.ID"
                  :value="server.ID"
                >
                  {{ server.NAME }}
                </option>
              </optgroup>
              <optgroup :label="t('bindRole.allServers')">
                <option
                  v-for="server in bindStore.serverList"
                  :key="server.ID"
                  :value="server.ID"
                >
                  {{ server.NAME }}
                </option>
              </optgroup>
            </select>
          </div>
        </div>
        <div class="row">
          <label class="label">{{ t("bindRole.role") }}</label>
          <div class="select-container">
            <select
              v-model="selectedRoleId"
              class="select-input"
              :disabled="!selectedServerId"
            >
              <option value="">
                {{
                  bindStore.roleList.length > 0 || !selectedServerId
                    ? t("bindRole.rolePlaceholder")
                    : t("bindRole.noRole")
                }}
              </option>
              <option
                v-for="role in bindStore.roleList"
                :key="role.role_id"
                :value="role.role_id"
              >
                {{ role.role_name }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn-cancel" @click="onCancel">
          {{ t("common.cancel") }}
        </button>
        <button
          class="btn-confirm"
          @click="onConfirm"
          :disabled="!selectedServerId || !selectedRoleId"
        >
          {{ t("common.confirm") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineProps, defineEmits, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useBindStore } from "@/stores/bind";
import { useToastStore } from "@/stores";
import { useUserStore } from "@/stores";
import { getUrlParams } from "@/utils";
import { clickButton, pageType } from "@/utils/point";

// 类型定义
interface ServerItem {
  ID: number;
  NAME: string;
  SID?: number;
  [key: string]: any;
}

interface RoleItem {
  role_id: string;
  role_name: string;
  [key: string]: any;
}

const props = defineProps<{ show: boolean }>();
const emits = defineEmits(["update:show"]);
const roleLoading = ref(false);
const { t } = useI18n();
const bindStore = useBindStore();
const toast = useToastStore();
const userStore = useUserStore();

// 动态标题计算属性
const modalTitle = computed(() => {
  if (userStore.bindInfo?.role_id) {
    // 已绑定角色，显示修改标题
    return t("bindRole.modifyTitle");
  } else {
    // 未绑定角色，显示绑定标题
    return t("bindRole.title");
  }
});

const selectedServerId = ref<string | number>("");
const selectedRoleId = ref<string | number>("");

// 获取选中的服务器和角色对象
const selectedServer = computed(() => {
  if (!selectedServerId.value) return null;

  // 先在最近服务器中查找
  const recentServer = (bindStore.recentServerList as ServerItem[]).find(
    (server: ServerItem) => server.ID === selectedServerId.value
  );
  if (recentServer) return recentServer;

  // 再在全部服务器中查找
  return (bindStore.serverList as ServerItem[]).find(
    (server: ServerItem) => server.ID === selectedServerId.value
  );
});

const selectedRole = computed(() => {
  if (!selectedRoleId.value) return null;
  return (bindStore.roleList as RoleItem[]).find(
    (role: RoleItem) => role.role_id === selectedRoleId.value
  );
});

// 初始化数据的函数
const initModalData = async () => {
  try {
    await bindStore.getServerList();

    // 获取服务器ID，优先使用bindInfo，其次使用URL参数
    const serverId =
      userStore.bindInfo?.server_id ||
      getUrlParams("serverId") ||
      getUrlParams("server_id") ||
      getUrlParams("serverID");

    if (serverId) {
      // 查找对应的服务器
      const allServers = [
        ...bindStore.recentServerList,
        ...bindStore.serverList,
      ];
      const currentServer = allServers.find(
        (server: ServerItem) =>
          Number(server.SID || server.ID) === Number(serverId)
      );

      if (currentServer) {
        selectedServerId.value = (currentServer as ServerItem).ID;

        // 获取角色列表并预填充
        try {
          await bindStore.getRoleList((currentServer as ServerItem).ID);

          // 获取角色ID，优先使用bindInfo，其次使用URL参数
          const roleId =
            userStore.bindInfo?.role_id ||
            getUrlParams("roleId") ||
            getUrlParams("role_id") ||
            getUrlParams("roleID");

          if (roleId) {
            // 查找对应的角色
            const currentRole = (bindStore.roleList as RoleItem[]).find(
              (role: RoleItem) => role.role_id == roleId
            );

            if (currentRole) {
              selectedRoleId.value = currentRole.role_id;
            }
          }
        } catch (error) {
          console.error("获取角色列表失败:", error);
        }
      }
    }
  } catch (error) {
    console.error("获取服务器列表失败:", error);
    toast.showToast(t("bindRole.getServerListFailed"), "error");
  }
};

watch(
  () => props.show,
  async (val) => {
    if (val) {
      // 当弹窗打开时，初始化数据
      await initModalData();
    } else {
      // 当弹窗关闭时，重置选择
      selectedServerId.value = "";
      selectedRoleId.value = "";
      roleLoading.value = false;
    }
  }
);

// 监听角色列表变化，如果只有一项则自动选中
watch(
  () => bindStore.roleList,
  (newRoleList) => {
    if (newRoleList.length === 1 && selectedServerId.value) {
      selectedRoleId.value = (newRoleList[0] as RoleItem).role_id;
    }
  },
  { deep: true }
);

const onServerChange = async () => {
  // 当服务器改变时，清空角色选择并重新获取角色列表
  selectedRoleId.value = "";
  roleLoading.value = true;
  if (selectedServerId.value) {
    try {
      await bindStore.getRoleList(selectedServerId.value);
      // 如果角色列表只有一项，自动选中
      if (bindStore.roleList.length === 1) {
        selectedRoleId.value = (bindStore.roleList[0] as RoleItem).role_id;
      }
    } catch (error) {
      console.error("获取角色列表失败:", error);
      toast.showToast(t("bindRole.getRoleListFailed"), "error");
    } finally {
      roleLoading.value = false;
    }
  }
};

const onConfirm = async () => {
  if (selectedServer.value && selectedRole.value) {
    try {
      clickButton({
        eventType: pageType.HOME,
        eventReason: "确认绑定角色",
        eventValue: JSON.stringify({
          serverID: selectedServer.value.ID,
          roleID: selectedRole.value.role_id,
          serverName: selectedServer.value.NAME,
          roleName: selectedRole.value.role_name,
        }),
      });
      const success = await bindStore.bindRole(
        selectedServer.value.ID,
        selectedRole.value.role_id,
        selectedServer.value.NAME,
        encodeURIComponent(encodeURIComponent(selectedRole.value.role_name))
      );
      if (success) {
        toast.showToast(t("bindRole.bindSuccess"), "success");
        userStore.getUserInfo();
        emits("update:show", false);
      } else {
        toast.showToast(t("bindRole.bindFailed"), "error");
      }
    } catch (error) {
      console.error("绑定角色失败:", error);
      toast.showToast(t("bindRole.bindFailed"), "error");
    }
  }
};

const onCancel = () => {
  emits("update:show", false);
};
</script>

<style scoped>
.modal-mask {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  width: 420px;
  max-width: 90vw;
  padding: 36px 28px 28px 28px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  position: relative;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.modal-title {
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 28px;
  color: #333;
}
.row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.label {
  font-size: 16px;
  font-weight: 500;
  margin-right: 12px;
  color: #333;
  min-width: 60px;
}
.select-container {
  flex: 1;
  position: relative;
}
.select-input {
  width: 100%;
  font-size: 16px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #fff;
  transition: all 0.3s ease;
  cursor: pointer;
}
.select-input:focus {
  border-color: #ff9900;
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 153, 0, 0.1);
}
.select-input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 36px;
}
.btn-cancel {
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  padding: 10px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-cancel:hover {
  background: #e8e8e8;
}
.btn-confirm {
  background: #ff9900;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  padding: 10px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-confirm:hover {
  background: #ff8c00;
  transform: translateY(-1px);
}
.btn-confirm:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}
</style>
